import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { SectionHeader } from "./Top3";
import { toast } from "sonner";
import { Mic, MicOff, Send, Volume2, VolumeX } from "lucide-react";
import { useVoiceRecorder } from "../lib/useVoiceRecorder";
import { getVoice } from "../lib/voicePref";

const b64ToBlob = (b64, mime = "audio/mpeg") => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

const QUICK_PROMPTS = [
  "Run today's briefing",
  "What's on fire?",
  "Who's waiting on me?",
  "Money to collect",
  "Read me my Top 3",
];

export default function AskOperator() {
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // {role, text}
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [speak, setSpeak] = useState(true);
  const audioRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const playAudio = (b64) => {
    if (!b64 || !speak) return;
    try {
      const url = URL.createObjectURL(b64ToBlob(b64));
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.play().catch(() => {});
      }
    } catch { /* ignore */ }
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const res = await api.assistantChat({ message: msg, session_id: sessionId, speak, voice: getVoice() });
      setSessionId(res.session_id);
      setMessages((m) => [...m, { role: "assistant", text: res.reply }]);
      if (res.audio_b64) playAudio(res.audio_b64);
    } catch (e) {
      toast("Assistant failed", { description: e?.response?.data?.detail || "" });
    } finally {
      setBusy(false);
    }
  };

  const handleVoiceBlob = async (blob) => {
    setBusy(true);
    try {
      const fd = new FormData();
      const ext = blob.type.includes("mp4") ? "m4a" : blob.type.includes("ogg") ? "ogg" : "webm";
      fd.append("audio", blob, `ask.${ext}`);
      if (sessionId) fd.append("session_id", sessionId);
      fd.append("speak", String(speak));
      fd.append("voice", getVoice());
      const res = await api.assistantVoice(fd);
      setSessionId(res.session_id);
      setMessages((m) => [
        ...m,
        { role: "user", text: res.transcript },
        { role: "assistant", text: res.reply },
      ]);
      if (res.audio_b64) playAudio(res.audio_b64);
    } catch (e) {
      toast("Voice ask failed", { description: e?.response?.data?.detail || "" });
    } finally {
      setBusy(false);
    }
  };

  const voice = useVoiceRecorder(handleVoiceBlob);

  return (
    <section data-testid="ask-operator">
      <SectionHeader
        code="AI"
        label="ASK OPERATOR"
        caption="Your assistant. Speak or type. She knows your businesses, your people, and today."
      />
      <div className="op-panel op-panel--glow p-6 md:p-8">
        <div
          ref={scrollRef}
          className="max-h-[380px] overflow-y-auto pr-2 space-y-4 mb-5"
          data-testid="ask-transcript"
        >
          {messages.length === 0 ? (
            <div className="text-[#5E657D] text-sm">
              Say <em className="text-white/70 not-italic">"Good morning, run today's briefing"</em> and she'll walk you through top priorities, money to collect, deadlines, waiting on, and one action that matters most.
            </div>
          ) : (
            messages.map((m, i) => (
              <div
                key={i}
                data-testid={`msg-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 border ${
                    m.role === "user"
                      ? "border-white/10 bg-white/[0.03] text-white"
                      : "border-[rgba(0,229,255,0.25)] bg-[rgba(0,229,255,0.03)] text-white"
                  }`}
                >
                  <div className={`op-tag mb-1 ${m.role === "assistant" ? "text-[#00E5FF]" : ""}`}>
                    {m.role === "user" ? "YOU" : "OPERATOR"}
                  </div>
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
                </div>
              </div>
            ))
          )}
          {busy && (
            <div className="op-tag op-flicker text-[#00E5FF]" data-testid="ask-thinking">
              THINKING…
            </div>
          )}
        </div>

        {/* Quick prompts */}
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {QUICK_PROMPTS.map((p) => (
              <button
                key={p}
                onClick={() => send(p)}
                disabled={busy}
                data-testid={`ask-quick-${p.replace(/\W+/g, "-").toLowerCase()}`}
                className="op-mono text-[10px] tracking-[0.24em] px-3 py-1.5 border border-white/10 text-[#A0A5B5] hover:text-white hover:border-white/25 transition"
              >
                {p.toUpperCase()}
              </button>
            ))}
          </div>
        )}

        {/* Input row */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onMouseDown={voice.start}
            onMouseUp={voice.stop}
            onMouseLeave={() => { if (voice.state === "recording") voice.stop(); }}
            onTouchStart={(e) => { e.preventDefault(); voice.start(); }}
            onTouchEnd={(e) => { e.preventDefault(); voice.stop(); }}
            disabled={busy}
            data-testid="ask-mic"
            data-recording={voice.state === "recording"}
            className={`shrink-0 inline-flex items-center gap-2 px-4 py-3 border transition-all select-none ${
              voice.state === "recording"
                ? "border-[#ef4444] text-[#ef4444] bg-[rgba(239,68,68,0.08)] op-p1-flash"
                : voice.state === "error"
                ? "border-[#F5A623]/60 text-[#F5A623]"
                : "border-white/15 text-white hover:border-[#00E5FF]/60 hover:text-[#00E5FF]"
            }`}
          >
            {voice.state === "error" ? <MicOff size={14} /> : <Mic size={14} />}
            <span className="op-mono text-[10px] tracking-[0.24em] hidden sm:inline">
              {voice.state === "recording" ? `${String(voice.elapsed).padStart(2, "0")}s` : "HOLD"}
            </span>
          </button>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask her anything — 'what's on fire?', 'who owes me?', 'read me my Top 3'"
            className="op-input"
            data-testid="ask-input"
            disabled={busy}
          />
          <button
            type="button"
            onClick={() => setSpeak(!speak)}
            data-testid="ask-toggle-speak"
            className={`shrink-0 p-3 border transition ${
              speak ? "border-[rgba(0,229,255,0.4)] text-[#00E5FF]" : "border-white/10 text-[#5E657D]"
            }`}
            title={speak ? "Voice on" : "Voice off"}
          >
            {speak ? <Volume2 size={14} /> : <VolumeX size={14} />}
          </button>
          <button
            onClick={() => send()}
            disabled={busy || !input.trim()}
            data-testid="ask-send"
            className="op-btn op-btn--primary inline-flex items-center gap-2 shrink-0 disabled:opacity-40"
          >
            <Send size={12} /> SEND
          </button>
        </div>

        <audio ref={audioRef} className="hidden" data-testid="ask-audio" />
        <div className="op-tag mt-3" data-testid="ask-voice-label">VOICE · <span className="op-mono">{getVoice().toUpperCase()}</span> · OPENAI TTS · MODULAR</div>
      </div>
    </section>
  );
}
